import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, Package, Calendar, MapPin, User, Plane, Ship, FileText, Download } from 'lucide-react';
import { apiCall, API_ENDPOINTS } from '../../config/api';
import { useAuth } from '../../context/AuthContext';
import { getCloudFrontUrl } from '../../utils/s3Upload';

const ShipmentDetails = () => {
  const { awbNo } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [shipment, setShipment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    // Get shipment data from localStorage cache first
    const cachedShipments = localStorage.getItem(`userShipments_${user.id}`);
    if (cachedShipments) {
      try {
        const shipments = JSON.parse(cachedShipments);
        const foundShipment = shipments.find(s => (s.awb_no || s.awbNo) === awbNo);
        if (foundShipment) {
          setShipment(foundShipment);
          setLoading(false);
          return;
        }
      } catch (err) {
        console.error('Error reading cached shipments:', err);
      }
    }
    
    // If not in cache, fetch from API
    fetchShipmentDetails();
  }, [awbNo, user.id]);

  const fetchShipmentDetails = async () => {
    try {
      setLoading(true);
      const response = await apiCall(`${API_ENDPOINTS.GET_USER_SHIPMENTS}?user_id=${user.id}`, {
        method: 'GET'
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        const shipmentsArray = data.data || [];
        
        // Cache shipments for future use
        localStorage.setItem(`userShipments_${user.id}`, JSON.stringify(shipmentsArray));
        
        const foundShipment = shipmentsArray.find(s => (s.awb_no || s.awbNo) === awbNo);
        
        if (foundShipment) {
          setShipment(foundShipment);
        } else {
          setError('Shipment not found');
        }
      } else {
        setError('Failed to fetch shipment details');
      }
    } catch (err) {
      console.error('Error fetching shipment:', err);
      setError('An error occurred while fetching shipment details');
    } finally {
      setLoading(false);
    }
  };

  const parseField = (field) => {
    if (!field) return [];
    if (Array.isArray(field)) return field;
    if (typeof field === 'string') {
      try {
        const parsed = JSON.parse(field);
        return Array.isArray(parsed) ? parsed : [field];
      } catch {
        return [field];
      }
    }
    return [field];
  };

  const formatNumber = (num) => {
    if (!num || num === '-' || num === '0') return num;
    const numStr = num.toString().replace(/,/g, '');
    const parts = numStr.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  };

  const handleDownload = async (awbNo, type) => {
    try {
      let pdfUrl = '';
      const leoValue = shipment.leoPdf || shipment.leoPdfKey || shipment.leo_pdf || null;
      const leoList = parseField(leoValue);
      
      // Get S3 key from shipment data
      if (type === 'awb' && shipment.awbPdf) {
        pdfUrl = getCloudFrontUrl(shipment.awbPdf);
      } else if (type === 'leo' && leoList.length > 0) {
        const urls = leoList.map((key) => getCloudFrontUrl(key)).filter(Boolean);
        if (urls.length > 0) {
          urls.forEach((url) => window.open(url, '_blank'));
          return;
        }
      } else if (type === 'invpl' && shipment.invPdf) {
        pdfUrl = getCloudFrontUrl(shipment.invPdf);
      }
      
      // If S3 URL found, open in new tab
      if (pdfUrl) {
        window.open(pdfUrl, '_blank');
        return;
      }
      
      // Fallback to old API endpoint if S3 key not available
      let endpoint = '';
      if (type === 'awb') endpoint = `${API_ENDPOINTS.DOWNLOAD_AWB}${awbNo}`;
      else if (type === 'leo') endpoint = `${API_ENDPOINTS.DOWNLOAD_LEO}${awbNo}`;
      else if (type === 'invpl') endpoint = `${API_ENDPOINTS.DOWNLOAD_INVPL}${awbNo}`;

      const response = await apiCall(endpoint, { method: 'GET' });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${awbNo}_${type}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download document. Please try again.');
    }
  };

  const joinWithAnd = (value) => {
    const arr = parseField(value);
    const filtered = arr.filter(item => item !== null && item !== undefined && item !== '');
    const unique = [...new Set(filtered)];
    return unique.join(' & ') || '-';
  };

  if (loading) {
    return (
      <div className="min-h-full bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading shipment details...</p>
        </div>
      </div>
    );
  }

  if (error || !shipment) {
    return (
      <div className="min-h-full bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Package className="mx-auto mb-4 text-red-500" size={48} />
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            {error || 'Shipment Not Found'}
          </h2>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-3 sm:py-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 sm:gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-3 sm:mb-4"
          >
            <ArrowLeft size={18} className="sm:hidden" />
            <ArrowLeft size={20} className="hidden sm:block" />
            <span className="text-sm sm:text-base">Back</span>
          </button>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                Shipment Details
              </h1>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
                AWB: {shipment.awbNo || shipment.awb_no}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleDownload(shipment.awbNo || shipment.awb_no, 'awb')}
                className="px-3 sm:px-4 py-1.5 sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm"
              >
                <Download size={14} className="sm:hidden" />
                <Download size={16} className="hidden sm:block" />
                AWB
              </button>
              <button
                onClick={() => handleDownload(shipment.awbNo || shipment.awb_no, 'leo')}
                className="px-3 sm:px-4 py-1.5 sm:py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm"
              >
                <Download size={14} className="sm:hidden" />
                <Download size={16} className="hidden sm:block" />
                LEO
              </button>
              <button
                onClick={() => handleDownload(shipment.awbNo || shipment.awb_no, 'invpl')}
                className="px-3 sm:px-4 py-1.5 sm:py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm"
              >
                <Download size={14} className="sm:hidden" />
                <Download size={16} className="hidden sm:block" />
                INV/PL
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6">
        {/* Route Header */}
        <div className="bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 dark:from-gray-700 dark:via-gray-650 dark:to-gray-600 rounded-xl sm:rounded-2xl p-4 sm:p-6 mb-4 sm:mb-6 relative overflow-hidden shadow-lg">
          <div className="absolute top-0 right-0 w-40 h-40 bg-white/20 dark:bg-black/10 rounded-full -mr-20 -mt-20"></div>
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/20 dark:bg-black/10 rounded-full -ml-16 -mb-16"></div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between relative z-10">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 flex-1">
              <div className="text-center">
                <p className="text-xs sm:text-sm text-white/90 dark:text-white/70 mb-1">From</p>
                <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white">{shipment.origin || joinWithAnd(shipment.origin)}</h3>
                <p className="text-[10px] sm:text-xs text-white/80 dark:text-white/60">{shipment.originCity || joinWithAnd(shipment.originCity)}, India</p>
              </div>
              
              <div className="flex-1 relative hidden sm:block">
                <div className="h-px bg-gradient-to-r from-white/30 via-white to-white/30 w-full"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-gray-700 px-2 sm:px-3 py-1 rounded-full border-2 border-white flex items-center gap-1 sm:gap-2 shadow-lg">
                  <Plane size={14} className="sm:hidden text-blue-600 dark:text-blue-400" />
                  <Plane size={18} className="hidden sm:block text-blue-600 dark:text-blue-400" />
                  <span className="text-xs sm:text-sm font-bold text-gray-900 dark:text-white">{shipment.flightNo || joinWithAnd(shipment.flight_voyage)}</span>
                </div>
              </div>
              
              {/* Mobile Flight Badge */}
              <div className="sm:hidden w-full flex justify-center my-2">
                <div className="bg-white/20 px-3 py-1.5 rounded-full border border-white/40 flex items-center gap-2">
                  <Plane size={14} className="text-white" />
                  <span className="text-xs font-bold text-white">{shipment.flightNo || joinWithAnd(shipment.flight_voyage)}</span>
                </div>
              </div>
              
              <div className="text-center">
                <p className="text-xs sm:text-sm text-white/90 dark:text-white/70 mb-1">To</p>
                <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white">{shipment.destination || joinWithAnd(shipment.destination)}</h3>
                <p className="text-[10px] sm:text-xs text-white/80 dark:text-white/60">{shipment.destCity || joinWithAnd(shipment.destCity)}, {shipment.destCountry || joinWithAnd(shipment.destCountry)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Details Grid - Same as Customer Dashboard Cards */}
        <div className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl shadow-lg p-3 sm:p-4 lg:p-6">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4">Shipment Information</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-5">
            {[
              { label: 'AWB No.', value: shipment.awbNo || shipment.awb_no },
              { label: 'S/B No.', value: shipment.sbNo || joinWithAnd(shipment.sb_no) },
              { label: 'S/B Date', value: shipment.sbDate || joinWithAnd(shipment.sb_date) },
              { label: 'Total Pkt', value: (shipment.totalPkt || joinWithAnd(shipment.no_of_pkgs)) && (shipment.totalPkt || joinWithAnd(shipment.no_of_pkgs)) !== '-' ? `${shipment.totalPkt || joinWithAnd(shipment.no_of_pkgs)} Pkt` : (shipment.totalPkt || joinWithAnd(shipment.no_of_pkgs)) },
              { label: 'Gr. Weight', value: (shipment.grWeight || formatNumber(joinWithAnd(shipment.gr_wt))) && (shipment.grWeight || formatNumber(joinWithAnd(shipment.gr_wt))) !== '-' ? `${shipment.grWeight || formatNumber(joinWithAnd(shipment.gr_wt))} Kg` : (shipment.grWeight || formatNumber(joinWithAnd(shipment.gr_wt))) },
              { label: 'Ch. Weight', value: (shipment.chWeight || formatNumber(joinWithAnd(shipment.ch_wt))) && (shipment.chWeight || formatNumber(joinWithAnd(shipment.ch_wt))) !== '-' ? `${shipment.chWeight || formatNumber(joinWithAnd(shipment.ch_wt))} Kg` : (shipment.chWeight || formatNumber(joinWithAnd(shipment.ch_wt))) },
              { label: 'FOB Value', value: shipment.fobValue || joinWithAnd(shipment.fob_value) },
              { label: 'DBK', value: (shipment.dbk || joinWithAnd(shipment.dbk)) && (shipment.dbk || joinWithAnd(shipment.dbk)) !== '-' ? `₹${shipment.dbk || joinWithAnd(shipment.dbk)}` : (shipment.dbk || joinWithAnd(shipment.dbk)) },
              { label: 'Rodtep', value: (shipment.rodtep || joinWithAnd(shipment.rodtep)) && (shipment.rodtep || joinWithAnd(shipment.rodtep)) !== '-' ? `₹${shipment.rodtep || joinWithAnd(shipment.rodtep)}` : (shipment.rodtep || joinWithAnd(shipment.rodtep)) },
              { label: 'Customer', value: shipment.customer || joinWithAnd(shipment.customer) },
              { label: 'IGST', value: (shipment.igst || joinWithAnd(shipment.igst)) && (shipment.igst || joinWithAnd(shipment.igst)) !== '-' ? `₹${shipment.igst || joinWithAnd(shipment.igst)}` : (shipment.igst || joinWithAnd(shipment.igst)) },
              { label: 'Rosctl', value: (shipment.rosctl || joinWithAnd(shipment.rosctl)) && (shipment.rosctl || joinWithAnd(shipment.rosctl)) !== '-' ? `₹${shipment.rosctl || joinWithAnd(shipment.rosctl)}` : (shipment.rosctl || joinWithAnd(shipment.rosctl)) }
            ].map((item, idx) => (
              <div 
                key={idx} 
                className="p-2.5 sm:p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-all"
              >
                <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mb-0.5 sm:mb-1 font-medium">{item.label}</p>
                <p className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white break-words">{item.value || '-'}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShipmentDetails;
